import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { useLazyQuery } from '@apollo/client'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  EngagementMySurveysDataCollectionDocument,
  EngagementSurveyDetailDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { convertDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { FormRender } from 'modules/forms'
import { FormResponsesSidepanel } from 'modules/forms/components/FormResponses/FormRenderResponses/FormResponsesSidepanel'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

export default function MySurveysListPage() {
  const { access } = useCurrentSession()
  const apolloClient = useApolloClient()
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const source = useGraphqlDataCollectionSource({
    document: EngagementMySurveysDataCollectionDocument,
    mapData: (data) => data.forms.membershipsConnection.nodes,
    mapPagination: (data) => {
      return {
        total: data.forms.membershipsConnection.nodes.length ?? 0,
      }
    },
    mapVariables: ({ filters, search, sortings }) => {
      const formType = filters.formType
      return {
        respondentId: access.id.toString(),
        search,
        formType,
        sort: sortings?.[0],
      }
    },
    itemOnClick: ({ id, materializedForm }) => {
      if (!materializedForm) {
        return () => {}
      }

      return () => navigate(navigation.mySurveys.membershipId.fullPath({ membershipId: id }))
    },
    sortings: {
      status: {
        label: i18n.t('surveys.surveys_table.columns.status'),
      },
    },
    defaultSortings: {
      field: 'status',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
  })

  const { membershipId } = useParams<{ membershipId: string }>()

  const [fetchOpenFormData, { data: openFormData }] = useLazyQuery(EngagementSurveyDetailDocument, {
    fetchPolicy: 'cache-first',
  })

  useEffect(() => {
    if (membershipId) {
      fetchOpenFormData({
        variables: {
          respondentId: access.id.toString(),
          membershipId: membershipId,
        },
      })
    }
  }, [membershipId, fetchOpenFormData, access.id])

  const handleCloseForm = () => {
    navigate(navigation.mySurveys.fullPath())
  }

  return (
    <ResponsivePage.Body loading={false} error={false} variant="full">
      <OneDataCollection
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: [
                {
                  label: i18n.t('surveys.surveys_table.columns.title'),
                  render: (item) => ({
                    type: 'text',
                    value: item.materializedForm?.form?.title,
                  }),
                },
                {
                  label: i18n.t('surveys.my_surveys.columns.received_at'),
                  render: ({ createdAt }) => {
                    return {
                      type: 'date',
                      value: convertDate(createdAt, 'backend_datetime', 'ui_dmy_long'),
                    }
                  },
                },
                {
                  label: i18n.t('surveys.surveys_table.columns.status'),
                  render: ({ status }) => {
                    const completed = status === 'completed'

                    return {
                      type: 'status',
                      value: {
                        label: completed
                          ? i18n.t('surveys.response_status.completed')
                          : i18n.t('surveys.response_status.pending'),
                        status: completed ? 'positive' : 'neutral',
                      },
                    } as const
                  },
                  sorting: 'status',
                },
              ],
            },
          },
        ]}
      />

      {openFormData &&
        membershipId &&
        (() => {
          const edges = openFormData.forms.membershipsConnection.edges
          const targetMembership = edges.find((edge) => edge.node.id === membershipId)?.node

          if (!targetMembership) return null

          const isCompleted = targetMembership.status === 'completed'

          return isCompleted ? (
            <FormResponsesSidepanel
              membershipId={targetMembership.id}
              materializedFormResourceType="Surveys::Survey"
              formId={targetMembership.materializedForm?.form?.id ?? ''}
              isOpen
              onClose={handleCloseForm}
              resourceType="Surveys::Survey"
            />
          ) : (
            <FormRender
              formId={targetMembership.materializedForm?.form?.id ?? ''}
              resourceType="Surveys::Survey"
              onClose={handleCloseForm}
              onSubmitSuccess={async () => {
                await apolloClient.refetchQueries({
                  include: [EngagementMySurveysDataCollectionDocument],
                })
                handleCloseForm()
              }}
              isOpen
              mode="answer"
              membershipId={targetMembership.id}
            />
          )
        })()}
    </ResponsivePage.Body>
  )
}
